// src/components/settings/SettingsContext.tsx
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { selectableSettingsTabs } from "./settingsTabs";

/**
 * open/close state for the settings drawer.
 */
export interface SettingsContextValue {
	open: boolean;
	activeTab: string;
	openSettings: (tab?: string) => void;
	closeSettings: () => void;
	openSettingsAcrossNavigation: (tab?: string) => void;
	consumeNavigationHandoff: () => boolean;
}

const SettingsContext = createContext<SettingsContextValue>({
	open: false,
	activeTab: "",
	openSettings: () => {},
	closeSettings: () => {},
	openSettingsAcrossNavigation: () => {},
	consumeNavigationHandoff: () => false,
});

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [open, setOpen] = useState(false);
	// Persists across open/close, so re-opening returns to the tab the practitioner left on.
	const [activeTab, setActiveTab] = useState("");

	const openSettings = useCallback(
		(tab?: string) => {
			const selectable = selectableSettingsTabs();
			if (selectable.length === 0) return;

			// Resolve against what this user can actually SEE, not against the registry:
			// a requested key that is gated off falls back to the first visible tab.
			const requested = tab ?? activeTab;
			const resolved = selectable.some((t) => t.key === requested) ? requested : selectable[0].key;

			setActiveTab(resolved);
			setOpen(true);
		},
		[activeTab],
	);

	const closeSettings = useCallback(() => setOpen(false), []);

	// A REF, not state: arming it must not re-render, and D7's effect has to read the value
	// written moments earlier in the same commit — which a state variable would not yet carry.
	const navigationHandoff = useRef(false);

	const openSettingsAcrossNavigation = useCallback(
		(tab?: string) => {
			navigationHandoff.current = true;
			openSettings(tab);
		},
		[openSettings],
	);

	const consumeNavigationHandoff = useCallback(() => {
		const armed = navigationHandoff.current;
		navigationHandoff.current = false;
		return armed;
	}, []);

	const value = useMemo<SettingsContextValue>(
		() => ({ open, activeTab, openSettings, closeSettings, openSettingsAcrossNavigation, consumeNavigationHandoff }),
		[open, activeTab, openSettings, closeSettings, openSettingsAcrossNavigation, consumeNavigationHandoff],
	);

	return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};

/** Read/drive the settings drawer. Outside the provider these are no-ops — never a throw. */
export const useSettings = (): SettingsContextValue => useContext(SettingsContext);
